import { useMemo } from 'react';
import { buildActionContext } from '../../../types/planManagement';
import type { PlanAppMode, BedPlantingInfo } from '../../../types/planManagement';
import { isBedEntity, getEntityDescription } from '../../../utils/planEntities';
import type { EntityInteractionState } from '../../../hooks/useEntityInteraction';

interface UseEntityPanelDataParams {
  appMode: PlanAppMode;
  entityState: EntityInteractionState;
  bedPlantingMap?: Map<string, BedPlantingInfo>;
  multiSelectedBeds: Set<string>;
}

export const useEntityPanelData = ({
  appMode,
  entityState,
  bedPlantingMap,
  multiSelectedBeds,
}: UseEntityPanelDataParams) => {
  const selectedBedInfo = useMemo(() => {
    if (appMode !== 'management') return null;
    if (!entityState.selectedEntity || !isBedEntity(entityState.selectedEntity)) return null;
    const bedId = entityState.selectedEntity.bedId;
    if (bedPlantingMap?.has(bedId)) return bedPlantingMap.get(bedId)!;
    return { bedId, plantings: [] } as BedPlantingInfo;
  }, [appMode, entityState.selectedEntity, bedPlantingMap]);

  const multiSelectedBedsInfo = useMemo(() => {
    if (appMode !== 'management') return new Map<string, BedPlantingInfo>();
    const result = new Map<string, BedPlantingInfo>();
    multiSelectedBeds.forEach((bedId) => {
      if (bedPlantingMap?.has(bedId)) result.set(bedId, bedPlantingMap.get(bedId)!);
      else result.set(bedId, { bedId, plantings: [] });
    });
    return result;
  }, [appMode, multiSelectedBeds, bedPlantingMap]);

  const bedsWithPlantings = useMemo(() => {
    const result = new Set<string>();
    bedPlantingMap?.forEach((info, bedId) => {
      const ctx = buildActionContext(info);
      if (!ctx.isEmpty || ctx.hasBlockingPlanting) {
        result.add(bedId);
      }
    });
    return result;
  }, [bedPlantingMap]);

  const entityHintText = useMemo(() => {
    if (!entityState.hoveredEntity) return '';

    if (appMode === 'management' && isBedEntity(entityState.hoveredEntity)) {
      const bedId = entityState.hoveredEntity.bedId;
      const info = bedPlantingMap?.get(bedId);
      if (info && info.plantings.length > 0) {
        const active = info.plantings.filter((p) => p.lifecycleStatus === 'active');
        if (active.length > 0) {
          return `${info.bedName || `Грядка #${bedId.slice(0, 8)}`} \u2022 ${active[0].cropName}`;
        }
      }
      return `${info?.bedName || `Грядка #${bedId.slice(0, 8)}`} \u2022 пусто`;
    }

    return getEntityDescription(entityState.hoveredEntity);
  }, [appMode, entityState.hoveredEntity, bedPlantingMap]);

  return { selectedBedInfo, multiSelectedBedsInfo, bedsWithPlantings, entityHintText };
};
